import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, Chip, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Snackbar,
  useMediaQuery, useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon, Close as CloseIcon, LocationOn as LocationIcon,
  Person as PersonIcon, Phone as PhoneIcon, Schedule as ScheduleIcon,
  NavigateBefore as PrevIcon, NavigateNext as NextIcon, Today as TodayIcon,
} from '@mui/icons-material';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { apiService } from '../../services/api';
import { to12h } from '../../utils/helpers';

moment.locale('es');
const localizer = momentLocalizer(moment);

const TIME_SLOTS = [
  '08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00',
  '12:00 - 13:00', '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00',
  '17:00 - 18:00',
];

const statusColors: Record<string, string> = {
  scheduled: '#1976d2',
  in_progress: '#ed6c02',
  completed: '#2e7d32',
  cancelled: '#d32f2f',
  no_show: '#757575',
};

const statusLabels: Record<string, string> = {
  scheduled: 'Programada',
  in_progress: 'En Progreso',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No Show',
};

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: any;
}

const TechnicianCalendar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View>(isMobile ? 'agenda' : 'week');

  // Create appointment dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    scheduledDate: '',
    timeSlot: '',
    street: '',
    city: '',
    notes: '',
    clientNotes: '',
  });

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({
    show: false, type: 'success', message: '',
  });

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const start = moment(currentDate).startOf('month').subtract(7, 'days').toISOString();
      const end = moment(currentDate).endOf('month').add(7, 'days').toISOString();
      const res = await apiService.getMyCalendar(start, end);
      const data = res.data || [];
      const mapped: CalendarEvent[] = data.map((appt: any) => {
        const date = new Date(appt.scheduledDate);
        const [startH] = (appt.timeSlot || '08:00').split(' - ');
        const [h, m] = startH.split(':').map(Number);
        const startTime = new Date(date);
        startTime.setHours(h, m, 0);
        const endTime = new Date(startTime);
        endTime.setHours(endTime.getHours() + 1);
        const clientName = appt.clientId?.name || '';
        return {
          id: appt._id,
          title: clientName ? `${to12h(appt.timeSlot)} - ${clientName}` : `${to12h(appt.timeSlot)} - ${appt.address?.city || 'Cita'}`,
          start: startTime,
          end: endTime,
          resource: appt,
        };
      });
      setEvents(mapped);
    } catch (e) {
      console.error('Error fetching calendar:', e);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event.resource);
    setDetailOpen(true);
  };

  const handleSelectSlot = (slotInfo: { start: Date }) => {
    const date = moment(slotInfo.start).format('YYYY-MM-DD');
    setFormData({ scheduledDate: date, timeSlot: '', street: '', city: '', notes: '', clientNotes: '' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.scheduledDate || !formData.timeSlot || !formData.street || !formData.city) {
      setAlert({ show: true, type: 'error', message: 'Fecha, horario, calle y ciudad son requeridos' });
      return;
    }
    setCreating(true);
    try {
      await apiService.createMyAppointment({
        scheduledDate: formData.scheduledDate,
        timeSlot: formData.timeSlot,
        address: { street: formData.street, city: formData.city },
        notes: formData.notes,
        clientNotes: formData.clientNotes,
      });
      setAlert({ show: true, type: 'success', message: 'Cita creada exitosamente' });
      setCreateOpen(false);
      fetchCalendar();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al crear la cita' });
    } finally {
      setCreating(false);
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => ({
    style: {
      backgroundColor: statusColors[event.resource?.status] || '#1976d2',
      borderRadius: '6px',
      border: 'none',
      color: 'white',
      fontSize: isMobile ? '0.75rem' : '0.85rem',
      padding: '2px 6px',
    },
  });

  const messages = useMemo(() => ({
    today: 'Hoy',
    previous: 'Anterior',
    next: 'Siguiente',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    agenda: 'Agenda',
    date: 'Fecha',
    time: 'Hora',
    event: 'Cita',
    noEventsInRange: 'No hay citas en este período',
  }), []);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>Calendario</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
          setFormData({ scheduledDate: moment().format('YYYY-MM-DD'), timeSlot: '', street: '', city: '', notes: '', clientNotes: '' });
          setCreateOpen(true);
        }} size={isMobile ? 'small' : 'medium'}>
          Nueva Cita
        </Button>
      </Box>

      {/* Mobile navigation */}
      {isMobile && (
        <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, mb: 2 }}>
          <IconButton onClick={() => setCurrentDate(d => moment(d).subtract(1, view === 'agenda' ? 'month' : 'week').toDate())}>
            <PrevIcon />
          </IconButton>
          <Button size="small" startIcon={<TodayIcon />} onClick={() => setCurrentDate(new Date())}>
            {moment(currentDate).format('MMM YYYY')}
          </Button>
          <IconButton onClick={() => setCurrentDate(d => moment(d).add(1, view === 'agenda' ? 'month' : 'week').toDate())}>
            <NextIcon />
          </IconButton>
        </Paper>
      )}

      {/* View toggle for mobile */}
      {isMobile && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {(['agenda', 'day', 'week'] as View[]).map(v => (
            <Chip
              key={v}
              label={v === 'agenda' ? 'Lista' : v === 'day' ? 'Día' : 'Semana'}
              onClick={() => setView(v)}
              color={view === v ? 'primary' : 'default'}
              variant={view === v ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      )}

      {/* Calendar */}
      <Paper sx={{ p: { xs: 0.5, sm: 2 }, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{
            height: isMobile ? 'calc(100vh - 280px)' : 600,
            '& .rbc-toolbar': {
              flexWrap: 'wrap',
              gap: 1,
              display: isMobile ? 'none' : 'flex',
            },
            '& .rbc-header': { fontSize: isMobile ? '0.7rem' : '0.85rem', padding: '4px' },
            '& .rbc-time-slot': { minHeight: isMobile ? 25 : 30 },
            '& .rbc-event': { minHeight: 20 },
            '& .rbc-agenda-view table': { fontSize: isMobile ? '0.8rem' : '0.9rem' },
          }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={setCurrentDate}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              eventPropGetter={eventStyleGetter}
              messages={messages}
              min={new Date(2020, 0, 1, 7, 0)}
              max={new Date(2020, 0, 1, 19, 0)}
              step={60}
              timeslots={1}
            />
          </Box>
        )}
      </Paper>

      {/* ===== CREATE APPOINTMENT DIALOG ===== */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
        fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nueva Cita
          <IconButton onClick={() => setCreateOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth label="Fecha" type="date" value={formData.scheduledDate}
              onChange={e => setFormData(p => ({ ...p, scheduledDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: moment().format('YYYY-MM-DD') }}
            />
            <FormControl fullWidth>
              <InputLabel>Horario</InputLabel>
              <Select value={formData.timeSlot} label="Horario"
                onChange={e => setFormData(p => ({ ...p, timeSlot: e.target.value }))}>
                {TIME_SLOTS.map(slot => (
                  <MenuItem key={slot} value={slot}>{to12h(slot)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth required label="Calle / Dirección" value={formData.street}
              onChange={e => setFormData(p => ({ ...p, street: e.target.value }))}
              placeholder="Calle Principal #123"
            />
            <TextField
              fullWidth required label="Ciudad" value={formData.city}
              onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
              placeholder="Santo Domingo"
            />
            <TextField
              fullWidth label="Notas" multiline rows={2} value={formData.notes}
              onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
              placeholder="Notas sobre la instalación..."
            />
            <TextField
              fullWidth label="Notas del Cliente" multiline rows={2} value={formData.clientNotes}
              onChange={e => setFormData(p => ({ ...p, clientNotes: e.target.value }))}
              placeholder="Indicaciones del cliente..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={creating || !formData.scheduledDate || !formData.timeSlot || !formData.street || !formData.city}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}>
            {creating ? 'Creando...' : 'Crear Cita'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== APPOINTMENT DETAIL DIALOG ===== */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth
        fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalle de Cita
          <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {moment(selectedEvent.scheduledDate).format('ddd DD MMM YYYY')}
                </Typography>
                <Chip
                  label={statusLabels[selectedEvent.status] || selectedEvent.status}
                  sx={{ backgroundColor: statusColors[selectedEvent.status], color: 'white' }}
                  size="small"
                />
              </Box>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ScheduleIcon fontSize="small" color="primary" />
                    <Typography variant="body1" fontWeight={500}>{to12h(selectedEvent.timeSlot)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="error" />
                    <Typography variant="body1">{selectedEvent.address?.street}, {selectedEvent.address?.city}</Typography>
                  </Box>
                </CardContent>
              </Card>

              {selectedEvent.clientId && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon fontSize="small" color="primary" />
                      <Typography variant="body1" fontWeight={500}>{selectedEvent.clientId.name}</Typography>
                    </Box>
                    {selectedEvent.clientId.phoneNumber && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="success" />
                        <Typography
                          variant="body1"
                          component="a"
                          href={`tel:${selectedEvent.clientId.phoneNumber}`}
                          sx={{ color: 'success.main', textDecoration: 'none' }}
                        >
                          {selectedEvent.clientId.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                    {selectedEvent.clientId.email && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                        {selectedEvent.clientId.email}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {selectedEvent.notes && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2"><strong>Notas:</strong> {selectedEvent.notes}</Typography>
                </Alert>
              )}
              {selectedEvent.clientNotes && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2"><strong>Notas del cliente:</strong> {selectedEvent.clientNotes}</Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={alert.show} autoHideDuration={5000} onClose={() => setAlert(p => ({ ...p, show: false }))}>
        <Alert severity={alert.type} onClose={() => setAlert(p => ({ ...p, show: false }))} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechnicianCalendar;
